import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listForms } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import MobileLanguageSelector from '../../components/mobile/MobileLanguageSelector';
import MobileProfileSheet from '../../components/mobile/MobileProfileSheet';
import MobileEmailVerificationBanner from '../../components/mobile/MobileEmailVerificationBanner';
import MobileFormCreationWizard from '../../components/mobile/MobileFormCreationWizard';
import type { FormListItem, FormType } from '../../types/form';

export default function MobileFormsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('mobile');
  const { user, logout } = useAuthStore();
  const { resolvedTheme, toggleTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [navigatingFormId, setNavigatingFormId] = useState<string | null>(null);

  const loadForms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listForms();
      setForms(data.forms || []);
    } catch (err) {
      console.error('Failed to load forms:', err);
      setError(t('formsPage.errorTitle'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const handleRetry = loadForms;

  const handleFormClick = (form: FormListItem) => {
    if (navigatingFormId) return; // Prevent double-tap
    setNavigatingFormId(form.id);
    if (form.form_type === 'live_quiz') {
      navigate(`/m/live-quiz/${form.id}`);
    } else {
      navigate(`/m/builder/${form.id}`);
    }
  };

  const getFormIcon = (type: FormType) => {
    switch (type) {
      case 'poll':
        return '📊';
      case 'quiz':
        return '🎯';
      case 'live_quiz':
        return '⚡';
      default:
        return '📝';
    }
  };

  const getFormTypeLabel = (type: FormType) => {
    const typeKey = type === 'live_quiz' ? 'liveQuiz' : type;
    return t(`formsPage.formTypes.${typeKey}`);
  };

  const getFormTypeColor = (type: FormType) => {
    switch (type) {
      case 'poll':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'quiz':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'live_quiz':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400';
      default:
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Email Verification Banner */}
      <MobileEmailVerificationBanner />

      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('formsPage.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {user?.displayName || user?.email}
            </p>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowMenu(true)}
              aria-label={t('common.openMenu')}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg touch-manipulation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" role="status" aria-label={t('formsPage.loading')} />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('formsPage.errorTitle')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-xs">
              {error}
            </p>
            <button
              onClick={handleRetry}
              className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium active:scale-95 transition-transform touch-manipulation"
            >
              {t('formsPage.retryButton')}
            </button>
          </div>
        ) : forms.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('formsPage.emptyTitle')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-xs">
              {t('formsPage.emptyDescriptionNew')}
            </p>
            <button
              onClick={() => setShowCreateWizard(true)}
              className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium active:scale-95 transition-transform touch-manipulation"
            >
              {t('formsPage.createFirst')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {forms.map((form) => {
              const isNavigating = navigatingFormId === form.id;
              return (
                <button
                  key={form.id}
                  onClick={() => handleFormClick(form)}
                  disabled={!!navigatingFormId}
                  className={`w-full bg-white dark:bg-gray-800 rounded-2xl p-4 text-left shadow-sm border border-gray-200 dark:border-gray-700 transition-all touch-manipulation ${
                    isNavigating ? 'opacity-70' : 'active:scale-[0.98]'
                  } ${navigatingFormId && !isNavigating ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${getFormTypeColor(form.form_type)}`}>
                      {isNavigating ? (
                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        getFormIcon(form.form_type)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {form.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getFormTypeColor(form.form_type)}`}>
                          {getFormTypeLabel(form.form_type)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        {t('formsPage.updated', { date: new Date(form.updated_at).toLocaleDateString() })}
                      </p>
                    </div>
                    {isNavigating ? (
                      <span className="text-xs text-gray-400 flex-shrink-0">{t('formsPage.opening')}</span>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Profile Sheet */}
      <MobileProfileSheet
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
      />

      {/* Menu Modal */}
      {showMenu && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl animate-slideUp safe-area-inset-bottom">
            <div className="flex justify-center py-3">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            <div className="px-4 pb-6">
              {/* User Info - Clickable to open profile */}
              <button
                onClick={() => {
                  setShowMenu(false);
                  setShowProfile(true);
                }}
                className="w-full flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl mb-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors touch-manipulation"
              >
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                    {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-semibold text-gray-900 dark:text-white truncate">
                    {user?.displayName || 'User'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Menu Items */}
              <div className="space-y-2">
                <button
                  onClick={() => {
                    toggleTheme();
                  }}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
                >
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                    {isDark ? (
                      <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {isDark ? t('menu.lightMode') : t('menu.darkMode')}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {isDark ? t('menu.switchToLight') : t('menu.switchToDark')}
                    </p>
                  </div>
                </button>

                {/* Language Selector */}
                <MobileLanguageSelector onClose={() => setShowMenu(false)} />

                {/* Settings Section */}
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  {/* Security */}
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/settings/security');
                    }}
                    className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
                  >
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 dark:text-white">{t('menu.security')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('menu.securityDesc')}</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* API Keys */}
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/settings/api-keys');
                    }}
                    className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
                  >
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 dark:text-white">{t('menu.apiKeys')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('menu.apiKeysDesc')}</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Teams */}
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/settings/teams');
                    }}
                    className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
                  >
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 dark:text-white">{t('menu.teams')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('menu.teamsDesc')}</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Invitations */}
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/settings/invitations');
                    }}
                    className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
                  >
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 dark:text-white">{t('menu.invitations')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('menu.invitationsDesc')}</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Sign Out Section */}
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                    className="w-full flex items-center gap-4 p-4 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors touch-manipulation"
                  >
                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-red-600 dark:text-red-400">{t('menu.signOut')}</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAB - Create New Form */}
      <button
        onClick={() => setShowCreateWizard(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg flex items-center justify-center touch-manipulation active:scale-95 transition-all z-40 safe-area-inset-bottom"
        aria-label={t('createWizard.title')}
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {/* Create Form Wizard */}
      <MobileFormCreationWizard
        isOpen={showCreateWizard}
        onClose={() => setShowCreateWizard(false)}
        onFormCreated={loadForms}
      />
    </div>
  );
}
